import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const PredictiveAnalytics = () => {
  return (
    <>
      <SEO 
        title="Predictive Analytics - AI-Powered Forecasting & Business Intelligence"
        description="Transform your business with advanced predictive analytics solutions. Leverage machine learning algorithms for accurate forecasting, trend analysis, and data-driven decision making."
        keywords="predictive analytics, AI forecasting, machine learning predictions, business intelligence, data science, trend analysis, predictive modeling, enterprise analytics"
      />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="page-container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-blush-light/40 rounded-full text-primary-red font-semibold mb-4">
              AI Hub
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-charcoal animate-fade-in">
              <span className="text-gradient">Predictive Analytics</span> Solutions
            </h1>
            <p className="text-xl text-titanium leading-relaxed animate-slide-up">
              Harness the power of advanced machine learning algorithms to forecast trends, anticipate outcomes, and make data-driven decisions with unprecedented accuracy.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="section-title">Transform Data into Actionable Foresight</h2>
              <p className="text-titanium mb-6">
                Predictive analytics leverages historical data, statistical algorithms, and machine learning techniques to identify the likelihood of future outcomes. Our solutions enable organizations to move from reactive to proactive decision-making, optimizing operations and gaining competitive advantages.
              </p>
              <p className="text-titanium mb-6">
                By analyzing patterns in your historical data, we help you anticipate customer behavior, forecast demand, identify risks, and discover opportunities before they become obvious to competitors.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-primary text-center">
                  Start Your Project
                </Link>
                <Link to="/ai-hub" className="btn-secondary text-center">
                  Explore AI Hub
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="card text-center">
                <div className="text-3xl font-bold text-primary-red mb-2">95%+</div>
                <div className="text-sm text-titanium">Forecast Accuracy</div>
              </div>
              <div className="card text-center">
                <div className="text-3xl font-bold text-primary-red mb-2">40%</div>
                <div className="text-sm text-titanium">Cost Reduction</div>
              </div>
              <div className="card text-center">
                <div className="text-3xl font-bold text-primary-red mb-2">3-6x</div>
                <div className="text-sm text-titanium">ROI Increase</div>
              </div>
              <div className="card text-center">
                <div className="text-3xl font-bold text-primary-red mb-2">24/7</div>
                <div className="text-sm text-titanium">Real-Time Analysis</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="py-16 md:py-20 bg-alabaster">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Core Predictive Capabilities</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Comprehensive analytical solutions powered by cutting-edge machine learning algorithms
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
                title: "Demand Forecasting",
                description: "Predict future demand patterns with high accuracy using time series analysis and advanced regression models."
              },
              {
                icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z",
                title: "Customer Behavior Prediction",
                description: "Anticipate customer actions, preferences, and lifetime value through behavioral pattern analysis."
              },
              {
                icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
                title: "Revenue Optimization",
                description: "Maximize revenue through dynamic pricing strategies and sales forecast optimization."
              },
              {
                icon: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z",
                title: "Risk Assessment",
                description: "Identify and quantify potential risks before they materialize using predictive risk modeling."
              },
              {
                icon: "M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z",
                title: "Churn Prevention",
                description: "Detect early warning signals of customer churn and implement proactive retention strategies."
              },
              {
                icon: "M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z",
                title: "Inventory Management",
                description: "Optimize stock levels and reduce carrying costs through accurate inventory predictions."
              },
              {
                icon: "M13 10V3L4 14h7v7l9-11h-7z",
                title: "Predictive Maintenance",
                description: "Anticipate equipment failures and schedule maintenance before breakdowns occur."
              },
              {
                icon: "M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z",
                title: "Market Trend Analysis",
                description: "Identify emerging market trends and opportunities before your competition."
              },
              {
                icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
                title: "Fraud Detection",
                description: "Detect fraudulent activities in real-time using anomaly detection algorithms."
              }
            ].map((capability, index) => (
              <div key={index} className="card group hover:shadow-xl transition-all duration-300">
                <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary-red group-hover:scale-110 transition-all duration-300">
                  <svg className="w-6 h-6 text-primary-red group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={capability.icon} />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-3 text-charcoal">{capability.title}</h3>
                <p className="text-sm text-titanium">{capability.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodologies & Technologies */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Advanced Methodologies & Technologies</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Leveraging cutting-edge algorithms and proven statistical techniques
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="card">
              <h3 className="text-xl font-semibold mb-4 text-charcoal">Machine Learning Algorithms</h3>
              <ul className="space-y-3">
                {[
                  "Random Forest & Gradient Boosting",
                  "Neural Networks & Deep Learning",
                  "Support Vector Machines (SVM)",
                  "XGBoost & LightGBM",
                  "Ensemble Methods & Model Stacking",
                  "Bayesian Networks"
                ].map((item, index) => (
                  <li key={index} className="flex items-start text-titanium">
                    <svg className="w-5 h-5 text-primary-red mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="card">
              <h3 className="text-xl font-semibold mb-4 text-charcoal">Statistical Techniques</h3>
              <ul className="space-y-3">
                {[
                  "Time Series Analysis (ARIMA, SARIMA)",
                  "Regression Analysis (Linear, Logistic, Polynomial)",
                  "Survival Analysis & Cox Models",
                  "Monte Carlo Simulations",
                  "Markov Chain Models",
                  "Clustering & Segmentation"
                ].map((item, index) => (
                  <li key={index} className="flex items-start text-titanium">
                    <svg className="w-5 h-5 text-primary-red mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="card">
              <h3 className="text-xl font-semibold mb-4 text-charcoal">Technology Stack</h3>
              <ul className="space-y-3">
                {[
                  "Python (Scikit-learn, TensorFlow, PyTorch)",
                  "R (caret, forecast, prophet)",
                  "Apache Spark for Big Data Processing",
                  "AWS SageMaker & Azure ML",
                  "SQL & NoSQL Databases",
                  "Real-Time Streaming Analytics"
                ].map((item, index) => (
                  <li key={index} className="flex items-start text-titanium">
                    <svg className="w-5 h-5 text-primary-red mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="card">
              <h3 className="text-xl font-semibold mb-4 text-charcoal">Model Validation</h3>
              <ul className="space-y-3">
                {[
                  "Cross-Validation & Backtesting",
                  "A/B Testing & Champion-Challenger",
                  "Performance Metrics (MAE, RMSE, R²)",
                  "Confusion Matrix & ROC Curves",
                  "Feature Importance Analysis",
                  "Continuous Model Monitoring"
                ].map((item, index) => (
                  <li key={index} className="flex items-start text-titanium">
                    <svg className="w-5 h-5 text-primary-red mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Applications */}
      <section className="py-16 md:py-20 bg-alabaster">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Industry Applications</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Tailored predictive analytics solutions across diverse industries
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                industry: "Retail & E-Commerce",
                applications: "Demand forecasting, personalized recommendations, price optimization, inventory management"
              },
              {
                industry: "Financial Services",
                applications: "Credit scoring, fraud detection, risk assessment, portfolio optimization, customer lifetime value"
              },
              {
                industry: "Healthcare",
                applications: "Patient readmission prediction, disease outbreak forecasting, treatment effectiveness, resource allocation"
              },
              {
                industry: "Manufacturing",
                applications: "Predictive maintenance, quality control, supply chain optimization, production planning"
              },
              {
                industry: "Telecommunications",
                applications: "Churn prediction, network optimization, usage forecasting, customer segmentation"
              },
              {
                industry: "Energy & Utilities",
                applications: "Load forecasting, equipment failure prediction, renewable energy output, consumption patterns"
              }
            ].map((item, index) => (
              <div key={index} className="card hover:shadow-xl transition-all duration-300">
                <h3 className="text-lg font-semibold mb-3 text-charcoal">{item.industry}</h3>
                <p className="text-sm text-titanium">{item.applications}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Business Benefits</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Measurable impact on your bottom line and operational efficiency
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6",
                title: "Increased Revenue",
                description: "Drive 15-30% revenue growth through optimized pricing and demand forecasting"
              },
              {
                icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
                title: "Cost Reduction",
                description: "Reduce operational costs by 25-40% through efficient resource allocation"
              },
              {
                icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
                title: "Risk Mitigation",
                description: "Identify and prevent potential issues before they impact operations"
              },
              {
                icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
                title: "Faster Decisions",
                description: "Make data-driven decisions 10x faster with real-time predictive insights"
              },
              {
                icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z",
                title: "Customer Retention",
                description: "Improve retention rates by 20-35% with proactive churn prevention"
              },
              {
                icon: "M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4",
                title: "Inventory Optimization",
                description: "Reduce inventory costs by 30-50% while maintaining service levels"
              },
              {
                icon: "M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z",
                title: "Innovation Edge",
                description: "Stay ahead of competitors with predictive market insights"
              },
              {
                icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
                title: "Scalable Growth",
                description: "Scale operations efficiently with predictive capacity planning"
              }
            ].map((benefit, index) => (
              <div key={index} className="card text-center group hover:shadow-xl transition-all duration-300">
                <div className="w-16 h-16 bg-blush-light/40 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-primary-red group-hover:scale-110 transition-all duration-300">
                  <svg className="w-8 h-8 text-primary-red group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={benefit.icon} />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2 text-charcoal">{benefit.title}</h3>
                <p className="text-sm text-titanium">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-16 md:py-20 bg-alabaster">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Our Implementation Process</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Structured approach to delivering high-impact predictive analytics solutions
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                step: "01",
                title: "Discovery & Assessment",
                description: "Understand business objectives, evaluate data quality, and identify key prediction targets"
              },
              {
                step: "02",
                title: "Data Preparation",
                description: "Clean, transform, and engineer features from historical data for optimal model performance"
              },
              {
                step: "03",
                title: "Model Development",
                description: "Build, train, and validate multiple models to identify the best-performing solution"
              },
              {
                step: "04",
                title: "Deployment & Monitoring",
                description: "Deploy models to production and establish continuous monitoring for optimal performance"
              }
            ].map((phase, index) => (
              <div key={index} className="card text-center hover:shadow-xl transition-all duration-300">
                <div className="text-4xl font-bold text-primary-red mb-4">{phase.step}</div>
                <h3 className="text-lg font-semibold mb-3 text-charcoal">{phase.title}</h3>
                <p className="text-sm text-titanium">{phase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study / Results */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <div className="card bg-alabaster">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-4 text-charcoal">Success Story</h2>
                <p className="text-lg text-titanium">Global Retail Chain - Demand Forecasting Transformation</p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-8 mb-8">
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary-red mb-2">42%</div>
                  <p className="text-titanium">Reduction in Stock-Outs</p>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary-red mb-2">$18M</div>
                  <p className="text-titanium">Annual Cost Savings</p>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary-red mb-2">97%</div>
                  <p className="text-titanium">Forecast Accuracy</p>
                </div>
              </div>

              <div className="prose max-w-none">
                <h3 className="text-xl font-semibold mb-3 text-charcoal">The Challenge</h3>
                <p className="text-titanium mb-6">
                  A major retail chain with 500+ stores struggled with inventory management, experiencing frequent stock-outs of popular items while maintaining excess inventory of slow-moving products. This resulted in lost sales, high carrying costs, and dissatisfied customers.
                </p>

                <h3 className="text-xl font-semibold mb-3 text-charcoal">Our Solution</h3>
                <p className="text-titanium mb-6">
                  We implemented an advanced predictive analytics system combining time series forecasting, machine learning algorithms, and real-time data integration. The solution analyzed historical sales data, seasonal patterns, promotional effects, weather conditions, and local events to generate accurate demand forecasts at the SKU-store level.
                </p>

                <h3 className="text-xl font-semibold mb-3 text-charcoal">The Impact</h3>
                <p className="text-titanium mb-4">
                  Within six months of implementation, the client achieved remarkable results: inventory costs decreased by 35%, stock-outs reduced by 42%, and overall customer satisfaction improved by 28%. The predictive system now processes over 2 million forecasts daily, automatically adjusting to market changes and seasonal trends.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related AI Hub Services */}
      <section className="py-16 md:py-20 bg-alabaster">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Related AI Capabilities</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Explore our comprehensive AI Hub offerings
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Intelligent Automation', link: '/ai-hub/intelligent-automation' },
              { title: 'Machine Learning', link: '/services/ai-machine-learning' },
              { title: 'Data Analytics', link: '/services/data-analytics' },
              { title: 'NLP Processing', link: '/ai-hub/nlp' }
            ].map((service, index) => (
              <Link 
                key={index}
                to={service.link}
                className="card text-center hover:shadow-xl transition-all duration-300 group"
              >
                <h3 className="text-lg font-semibold text-charcoal group-hover:text-primary-red transition-colors">
                  {service.title}
                </h3>
                <p className="text-sm text-primary-red mt-2 group-hover:underline">
                  Learn More →
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-primary-red text-white">
        <div className="page-container text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            Ready to Unlock Predictive Insights?
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
            Transform your data into actionable foresight. Let our experts help you build predictive analytics solutions that drive measurable business results.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-primary-red px-8 py-4 rounded-lg text-lg font-semibold hover:bg-pearl-white transition-all duration-300 shadow-lg">
              Schedule Consultation
            </Link>
            <Link to="/ai-hub" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-primary-red transition-all duration-300">
              Explore AI Hub
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default PredictiveAnalytics;